// frontend/src/hooks/usePipelineMonitor.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import { PipelineService } from '../services/pipeline.service';
import type { PipelineMetric, StatusEtapa } from '../interfaces/pipeline.interface';

// ============================================================
// REGRA DE PARADA DO POLLING
// ============================================================

const STATUS_EM_ANDAMENTO: StatusEtapa[] = ['STARTED', 'RUNNING'];

const devePararPolling = (linhas: PipelineMetric[]): boolean => {
  if (!linhas || linhas.length === 0) return false;

  const porStage = new Map<string, PipelineMetric>();
  for (const linha of linhas) {
    const atual = porStage.get(linha.stage);
    if (!atual || new Date(linha.created_at) > new Date(atual.created_at)) {
      porStage.set(linha.stage, linha);
    }
  }
  const etapas = Array.from(porStage.values());

  if (etapas.some(e => e.status === 'FAILED')) return true;
  if (etapas.some(e => STATUS_EM_ANDAMENTO.includes(e.status))) return false;
  return true;
};

// ============================================================
// HOOK
// ============================================================

export const usePipelineMonitor = (userId: number | null) => {
  const [historico, setHistorico] = useState<PipelineMetric[]>([]);
  const [execucaoAtiva, setExecucaoAtiva] = useState<PipelineMetric[]>([]);
  const [traceIdAtivo, setTraceIdAtivo] = useState<string | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [carregandoExecucao, setCarregandoExecucao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [monitorando, setMonitorando] = useState(false);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingActiveRef = useRef<boolean>(false);
  const requestIdRef = useRef<number>(0);

  // Guarda o userId mais recente para uso dentro dos callbacks, sem dependência
  const userIdRef = useRef<number | null>(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Guarda o traceId mais recente
  const traceIdRef = useRef<string | null>(null);
  useEffect(() => {
    traceIdRef.current = traceIdAtivo;
  }, [traceIdAtivo]);

  const pararPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingActiveRef.current = false;
  }, []);

  // ------------------------------------------------------------
  // Histórico
  // ------------------------------------------------------------

  const buscarHistorico = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) return;
    try {
      setCarregandoHistorico(true);
      setErro(null);
      const data = await PipelineService.obterHistorico(uid);
      setHistorico(data);
    } catch (err: any) {
      const msg =
        err.response?.data?.error || err.message || 'Erro ao buscar histórico';
      setErro(msg);
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  // ------------------------------------------------------------
  // Execução — não depende do userId estar pronto (usa ref)
  // ------------------------------------------------------------

  const buscarExecucao = useCallback(
    async (traceId: string): Promise<PipelineMetric[]> => {
      const uid = userIdRef.current;
      if (!uid) return [];

      try {
        setCarregandoExecucao(true);
        const data = await PipelineService.obterExecucao(uid, traceId);
        setErro(null);
        return data;
      } catch (err: any) {
        // 404 = trace ainda sem linhas em pipeline_metrics (race pós-ORC)
        // NÃO seta erro — deixa o polling tentar de novo
        if (err.response?.status === 404) {
          return [];
        }
        const msg =
          err.response?.data?.error || err.message || 'Erro ao buscar execução';
        setErro(msg);
        return [];
      } finally {
        setCarregandoExecucao(false);
      }
    },
    []
  );

  // ------------------------------------------------------------
  // Atualização periódica — atualiza execução E histórico juntos
  // ------------------------------------------------------------

  const atualizarExecucaoAtiva = useCallback(async () => {
    const trace = traceIdRef.current;
    const uid = userIdRef.current;

    // Se ainda não tem trace ou user, apenas espera (não mata o polling)
    if (!trace || !uid) return;

    const data = await buscarExecucao(trace);
    setExecucaoAtiva(data);

    // Atualiza a lista da esquerda junto com o polling
    await buscarHistorico();

    if (devePararPolling(data)) {
      pararPolling();
      setMonitorando(false);
      await buscarHistorico();
    }
  }, [buscarExecucao, pararPolling, buscarHistorico]);

  // ------------------------------------------------------------
  // Iniciar monitoramento
  // ------------------------------------------------------------

  const iniciarMonitoramento = useCallback(
    async (traceId: string) => {
      const myRequestId = ++requestIdRef.current;
      pararPolling();

      setTraceIdAtivo(traceId);
      traceIdRef.current = traceId;
      setMonitorando(true);
      setErro(null);

      const data = await buscarExecucao(traceId);

      if (myRequestId !== requestIdRef.current) return;

      setExecucaoAtiva(data);

      // Só para o polling se tem dados E já terminou
      if (data.length > 0 && devePararPolling(data)) {
        setMonitorando(false);
        await buscarHistorico();
        return;
      }

      // Sempre inicia o polling (mesmo vazio, mesmo sem user ainda)
      isPollingActiveRef.current = true;
      pollingIntervalRef.current = setInterval(atualizarExecucaoAtiva, 3000);
    },
    [buscarExecucao, pararPolling, atualizarExecucaoAtiva, buscarHistorico]
  );

  // ------------------------------------------------------------
  // Parar monitoramento (manual)
  // ------------------------------------------------------------

  const pararMonitoramento = useCallback(() => {
    requestIdRef.current++;
    pararPolling();
    setMonitorando(false);
    setTraceIdAtivo(null);
    traceIdRef.current = null;
    setExecucaoAtiva([]);
  }, [pararPolling]);

  // ------------------------------------------------------------
  // Atualização manual
  // ------------------------------------------------------------

  const atualizarManual = useCallback(async () => {
    if (!traceIdAtivo) return;

    const data = await buscarExecucao(traceIdAtivo);
    setExecucaoAtiva(data);

    if (devePararPolling(data)) {
      pararPolling();
      setMonitorando(false);
      await buscarHistorico();
    }
  }, [traceIdAtivo, buscarExecucao, pararPolling, buscarHistorico]);

  // ------------------------------------------------------------
  // Efeitos
  // ------------------------------------------------------------

  useEffect(() => {
    if (userId) {
      buscarHistorico();
    }
    return () => {
      pararPolling();
      requestIdRef.current++;
    };
  }, [userId, buscarHistorico, pararPolling]);

  return {
    historico,
    execucaoAtiva,
    traceIdAtivo,
    monitorando,
    carregandoHistorico,
    carregandoExecucao,
    erro,
    buscarHistorico,
    iniciarMonitoramento,
    pararMonitoramento,
    atualizarManual,
  };
};